import React, { useEffect, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import OriginTracing from '@atlassiansox/origin-tracing';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Text, xcss } from '@atlaskit/primitives';
import { LinkButton, type LinkButtonProps } from '@atlaskit/button/new';

import { SectionMessage } from '../UpsellLooknFeel/SectionMessage';
import { GradientCTAButton } from '../GradientLooknFeel/GradientCTAButton';
import { GradientCTAInvertedButton } from '../GradientLooknFeel/GradientCTAInvertedButton';
import { SectionMessageButtonConfig } from '../SectionMessage/ChangeEditionSectionMessage';

export const i18n = defineMessages({
	upsellButtonText: {
		id: 'change-edition.public-links.upsell.upgrade-button-text',
		defaultMessage: 'Upgrade',
		description: 'Button text to redirect users to upgrade from free or trial instances',
	},
	upsellLearnMoreButtonText: {
		id: 'change-edition.public-links.upsell.upgrade-learn-more-button-text',
		defaultMessage: 'Learn more',
		description: 'Button text to redirect users to learn more information about public links',
	},
	upsellTitle: {
		id: 'change-edition.public-links.upsell.share-page-title',
		defaultMessage: 'Share this content publicly',
		description:
			'Title to upsell for the Public Links feature, describing how Public Links are a way to share the page',
	},
	upsellDescription: {
		id: 'change-edition.public-links.upsell.share-page-description',
		defaultMessage: 'Your team can use public links to share content with anyone on the internet.',
		description:
			'Description to upsell for the Public Links feature, describing how the page is public to the internet',
	},
});

const PUBLIC_LINK_LEARN_MORE_URL =
	'https://support.atlassian.com/confluence-cloud/docs/share-content-externally-with-public-links';

export const ADMIN_BILLING_URL = '/admin/billing';

const containerStyles = xcss({
	marginTop: 'space.200',
});

type PublicLinksUpsellProps = {
	isSiteAdmin: boolean;
	upsellSource?: string;
};

const TEXT_MIN_WIDTH = '59px';
const TEXT_MAX_WIDTH = '155px';

export const PublicLinksUpsellButton = ({ inverted = true }: { inverted?: boolean }) => {
	const { formatMessage } = useIntl();
	const origin = new OriginTracing({ product: 'confluence' });

	return inverted ? (
		<GradientCTAInvertedButton
			href={origin.addToUrl(ADMIN_BILLING_URL)}
			target="_blank"
			textMinWidth={TEXT_MIN_WIDTH}
			textMaxWidth={TEXT_MAX_WIDTH}
		>
			{formatMessage(i18n.upsellButtonText)}
		</GradientCTAInvertedButton>
	) : (
		<GradientCTAButton href={origin.addToUrl(ADMIN_BILLING_URL)} target="_blank">
			{formatMessage(i18n.upsellButtonText)}
		</GradientCTAButton>
	);
};

export const PublicLinksLearnMoreButton = ({
	appearance = 'default',
	href = PUBLIC_LINK_LEARN_MORE_URL,
	target = '_blank',
	onClick = () => {},
	children,
}: Partial<LinkButtonProps>) => {
	const { formatMessage } = useIntl();

	return (
		<LinkButton appearance={appearance} href={href} target={target} onClick={onClick}>
			{children ?? formatMessage(i18n.upsellLearnMoreButtonText)}
		</LinkButton>
	);
};

export const PublicLinksUpsell = ({
	upsellSource = 'publicLinksUpsell',
	appearance,
	href,
	target,
	onClick = () => {},
}: PublicLinksUpsellProps & Partial<LinkButtonProps>) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		// https://data-portal.internal.atlassian.com/analytics/registry/73864
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'shown',
				actionSubject: 'upsell',
				actionSubjectId: 'publicLinksUpsell',
				source: 'publicLinksUpsell',
				attributes: {
					touchpointId: upsellSource,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, upsellSource]);

	const handleLearnMoreLinkClicked = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			// https://data-portal.internal.atlassian.com/analytics/registry/28345
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'learnMoreLink',
					source: 'publicLinksUpsell',
					attributes: {
						touchpointId: upsellSource,
					},
				},
			}).fire();
			onClick(e, analyticsEvent);
		},
		[createAnalyticsEvent, onClick, upsellSource],
	);

	return (
		<Flex direction="column">
			<SectionMessage
				withGradient={false}
				title={formatMessage(i18n.upsellTitle)}
				body={<Text>{formatMessage(i18n.upsellDescription)}</Text>}
				buttonConfig={SectionMessageButtonConfig.NONE} // hide default buttons of SectionMessage
				source={upsellSource}
				customPadding="0"
			/>
			<Flex direction="row" gap="space.050" xcss={containerStyles}>
				<PublicLinksLearnMoreButton
					appearance={appearance || 'default'}
					href={href}
					target={target}
					onClick={handleLearnMoreLinkClicked}
				>
					{formatMessage(i18n.upsellLearnMoreButtonText)}
				</PublicLinksLearnMoreButton>
			</Flex>
		</Flex>
	);
};
